import { http } from 'interfaces'
import { type LegDistrict, type LegSeatStatus, type Role } from 'services'
import { type ExecOrgParentLutItem } from 'services/executives/executiveOrgLookupService'
import { SerializableMap } from 'components'
import { type State } from 'components/us/common/state-select'
import { type UrlType } from '../utils'

export interface LutValues {
  currentTime?: string
}
export interface PositionLutValues extends LutValues {
  positionTypes: PositionType[]
}

export interface PositionType {
  id: number
  typeDescLong: string
  typeDescShort: string
  typeCode: string

}
export interface LegRole extends LutValues {
  id: number
  descShort: string
  descLong: string
  code: string
  lutOrder: number
  updated_by: string
  last_updated: string
  role: Role
}

export interface ExecRole {
  id: number
  descShort: string
  descLong: string
  lutOrder?: number
  role: Role
}

export interface ExecPosType {
  id: number
  code: string
  desc: string
  lutOrder?: number
}

export interface UsUrlRoleDescType {
  id: number
  role: Role
  type: UrlType
  desc: string
  contentType: string | null
  lutOrder: number
  updatedBy: string
  lastUpdated: string
}

export interface UsUrlDescTypeLutValues extends LutValues {
  descTypes: Map<number, UsUrlRoleDescType>
}

export interface LegRoleValues extends LutValues {
  legRoles: LegRole[]
}

export interface LegSeatStatusValues extends LutValues {
  statuses: LegSeatStatus[]
}

export interface PoliticalParty extends LutValues {
  id: number
  masterCode: string
  code: string
  desc: string
  lutOrder: number
}

export interface PoliticalPartyValues extends LutValues {
  parties: PoliticalParty[]
}
export interface LegDistrictValues extends LutValues {
  districts: Map<number, LegDistrict>
}

export interface CustomBehaviour {
  id: number
  behaviour_key: string
  ids: number[]
  description: string
  last_updated: string
}

export interface CustomBehaviourValues extends LutValues {
  custom_behaviours: Map<string, CustomBehaviour>
}

export interface RoleResponsibility {
  id: number
  knowWho: string
  descShort: string
  descLong: string
  lutOrder: number
  porS: string
  updated_by: string
  last_updated: string
}

export interface RoleResponsibilityValues extends LutValues {
  responsibilities: Map<string, RoleResponsibility>
}

export interface AddressType {
  id: number
  type_desc: string
  lut_order: number
  role: Role | null
}

export interface RoleAddressTypeLutValues extends LutValues {
  address_types: Record<number, AddressType>
}

export interface Building {
  id: number
  displayName?: string | null
  buildingName?: string | null
  room?: string | null
  street?: string | null
  street2?: string | null
  city?: string | null
  state?: string | null
  country?: number | null
  zipCode?: string | null
  legType?: string | null
  chamber?: string | null
  updatedBy?: string | null
  lastUpdated?: string | null
}

export interface BuildingLutValues extends LutValues {
  buildings: Building[]
}

export interface ExecOrgParentLutValues extends LutValues {
  parents: Map<number, ExecOrgParentLutItem>
}

export interface ExecRoleLutValues extends LutValues {
  execRoles: ExecRole[]
}

export type ProtectedRoleScope = 'NATIONAL' | 'STATE' | 'TOP_ORG'

export interface ProtectedRoleHolder {
  roleId: number
  scope: ProtectedRoleScope
  scopeValue: number
  positionId: number
  holderName: string
}

export interface ProtectedRoleHoldersLutValues extends LutValues {
  holders: ProtectedRoleHolder[]
}

export interface ExecPosTypeLutValues extends LutValues {
  execPosTypes: ExecPosType[]
}

export interface UsStateLutValues extends LutValues {
  states: Map<number, State>
}

export async function positionLutValueList (): Promise<PositionLutValues> {
  return await http.get('/api/v1/organizations/position/type/').then(value => value.data)
}

export async function legroleLutValueList (): Promise<LegRoleValues> {
  return await http.get('/api/v1/us/legrole/lut').then(value => value.data)
}

export async function legseatstatusLutValueList (): Promise<LegSeatStatusValues> {
  return await http.get('/api/v1/us/seatstatus/lut').then(value => value.data)
}

export async function usPoliticalPartiesLutValueList (): Promise<PoliticalPartyValues> {
  return await http.get('/api/v1/us/parties/lut').then(value => value.data)
}

export async function usLegDistrictsLutValueList (): Promise<LegDistrictValues> {
  const response = await http.get('/api/v1/us/districts/lut/')
  return { ...response.data, districts: new SerializableMap(response.data.districts.map((district: LegDistrict) => [district.id, district])) }
}

export async function customBehaviourLutValueList (): Promise<CustomBehaviourValues> {
  const response = await http.get('/api/v1/custom-behavior/lut/')
  return { ...response.data, custom_behaviours: new SerializableMap(response.data.custom_behaviours.map((customBehaviour: CustomBehaviour) => [customBehaviour.behaviour_key, customBehaviour])) }
}

export async function roleResponsibilityLutValueList (): Promise<RoleResponsibilityValues> {
  const response = await http.get('/api/v1/us/responsibilities/lut/')
  return { ...response.data, responsibilities: new SerializableMap(response.data.responsibilities.map((responsibility: RoleResponsibility) => [responsibility.id, responsibility])) }
}

export async function roleUrlDescLutValueList (): Promise<UsUrlDescTypeLutValues> {
  const response = await http.get('/api/v1/us/url/lut/')
  return { ...response.data, descTypes: new SerializableMap(response.data.descTypes.map((descType: UsUrlRoleDescType) => [descType.id, descType])) }
}

export async function roleAddressTypeLutValueList (): Promise<RoleAddressTypeLutValues> {
  return await http.get('/api/v1/us/addresstype/lut/').then(value => value.data)
}

export async function buildingLutValueList (): Promise<BuildingLutValues> {
  return await http.get('/api/v1/us/building/lut/').then(value => value.data)
}

export async function getExecutiveOrgParentsLut (): Promise<ExecOrgParentLutValues> {
  const response = await http.get('/api/v1/us/executives/orgs/parents/lut/')
  return { ...response.data, parents: new SerializableMap(response.data.parents.map((parent: ExecOrgParentLutItem) => [parent.id, parent])) }
}

export async function getExecRolesLut (): Promise<ExecRoleLutValues> {
  return await http.get('/api/v1/us/executives/exec-roles/lut/').then(value => value.data)
}

export async function getProtectedRoleHolders (): Promise<ProtectedRoleHoldersLutValues> {
  return await http.get('/api/v1/us/executives/protected-roles/holders/').then(value => value.data)
}

export async function getExecPositionTypesLut (): Promise<ExecPosTypeLutValues> {
  return await http.get('/api/v1/us/executives/position-types/lut/').then(value => value.data)
}

export async function getUsStatesLut (): Promise<UsStateLutValues> {
  const response = await http.get('/api/v1/us/states/lut/')
  const states: State[] = response.data.states.map((state: State) =>
    state.id === 0 && !(state.desc ?? '').toLowerCase().includes('federal')
      ? { ...state, desc: `${state.desc ?? 'All'} (federal)` }
      : state
  )
  return { ...response.data, states: new SerializableMap(states.map(state => [state.id, state])) }
}
